import type React from 'react'
import { useEffect, useState, useCallback } from 'react'
import type { ConversationPlan, Conversation } from '@knownow/domain'
import { Button } from '@knownow/ui'
import { listConversationsByPlan } from '@/lib/plan-api-client'
import { ConversationDrillIn } from '@/components/conversation-drill-in'

export interface ResponsesTabProps {
  readonly plan: ConversationPlan
}

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(start: Date, end: Date | null): string {
  if (!end) return 'In progress'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  const mins = Math.round(ms / 60000)
  return `${mins}m`
}

export function ResponsesTab({ plan }: ResponsesTabProps): React.ReactElement {
  const [conversations, setConversations] = useState<readonly Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (!plan.shareToken) {
      setLoading(false)
      return
    }

    listConversationsByPlan(plan.organizationId, plan.id).then((convs) => {
      setConversations(convs)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })
  }, [plan.id, plan.organizationId, plan.shareToken])

  const handleBack = useCallback((): void => {
    setSelectedId(null)
  }, [])

  if (!plan.shareToken) {
    return (
      <div className="flex flex-col items-center justify-center py-12" data-testid="empty-responses">
        <p className="text-sm text-neutral-500 mb-2">
          No responses yet. Publish your plan to start collecting responses.
        </p>
      </div>
    )
  }

  if (selectedId) {
    return (
      <div className="flex flex-col h-full">
        <div className="border-b border-neutral-200 px-4 py-2">
          <Button variant="secondary" size="sm" onClick={handleBack} data-testid="back-to-list">
            Back to list
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationDrillIn conversationId={selectedId} plan={plan} />
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-neutral-500">Loading responses...</p>
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12" data-testid="no-conversations">
        <p className="text-sm text-neutral-500">
          No conversations yet. Share the link to start collecting responses.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-neutral-200 px-4 py-3">
        <p className="text-sm font-medium text-neutral-700">
          {conversations.length} response{conversations.length !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm" data-testid="responses-table">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs font-medium text-neutral-500">
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Started</th>
              <th className="px-4 py-2">Duration</th>
            </tr>
          </thead>
          <tbody>
            {conversations.map((conv) => (
              <tr
                key={conv.id}
                onClick={() => setSelectedId(conv.id)}
                className="border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 transition-colors"
                data-testid={`conversation-row-${conv.id}`}
              >
                <td className="px-4 py-3 font-mono text-xs text-neutral-600">
                  {conv.id}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      conv.status === 'completed'
                        ? 'bg-success-light text-success-dark'
                        : conv.status === 'active'
                          ? 'bg-brand-100 text-brand-700'
                          : 'bg-neutral-200 text-neutral-600',
                    ].join(' ')}
                  >
                    {conv.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600">
                  {formatDate(conv.startedAt)}
                </td>
                <td className="px-4 py-3 text-neutral-600">
                  {formatDuration(conv.startedAt, conv.completedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
